import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db, auth, getCollectionName } from './firebase';
import {
  collection,
  onSnapshot,
  query,
  addDoc,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  orderBy
} from './firestore-compat';
import {
  addDays,
  addWeeks,
  addMonths,
  isBefore,
  isEqual,
  format
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

import AddAuditoriaModal from './AddAuditoriaModal';
import DeadlineReminder from './components/DeadlineReminder';
import { useDeadlineReminder } from './hooks/useDeadlineReminder';
import JustificativaModal from './JustificativaModal';
import BloquearDiaModal from './BloquearDiaModal';
import DesbloquearDiaModal from './DesbloquearDiaModal';
import {
  pedirPermissaoNotificacao,
  verificarAgendaParaNotificacoes
} from './notifications';
import NotificationsPopup from './components/NotificationsPopup';
import { notifyParticipantsOfChange } from './utils/changeNotificationUtils';

// ==========================
// FUNÇÃO PARA GERAR GRADE
// ==========================
const generateWeekdayGrid = (date) => {
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  let dayOfWeek = firstDayOfMonth.getDay();
  let diff = firstDayOfMonth.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  const gridStartDate = new Date(firstDayOfMonth.setDate(diff));

  const monthDays = [];
  let currentDate = new Date(gridStartDate);

  for (let i = 0; i < 35; i++) { // Aumentei para 35 para garantir 5 semanas visuais se necessário
    monthDays.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
    if (currentDate.getDay() === 6) {
      currentDate.setDate(currentDate.getDate() + 2);
    }
  }
  return monthDays;
};

// ==========================
// CLASSE DE STATUS
// ==========================
const getAuditoriaStatusClass = (auditoria) => {
  if (auditoria.isManualPlaceholder) return 'status-demanda-interna';
  if (auditoria.status === 'Feito') return 'status-feito';
  if (auditoria.status === 'Não Realizado' || auditoria.justificativaNaoRealizado)
    return 'status-nao-realizado';

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dataAuditoria = new Date(auditoria.data + 'T00:00:00');

  if (dataAuditoria < hoje && auditoria.status === 'Agendado')
    return 'status-nao-realizado';

  return '';
};

// ==========================
// COMPONENTE PRINCIPAL
// ==========================
function AgendaQualidade({ isPersonalView = false }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Hook para detectar mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // DADOS DINÂMICOS
  const [auditoresConfig, setAuditoresConfig] = useState([]);
  const [activeAuditores, setActiveAuditores] = useState([]); // Array de IDs ou Nomes

  const [unidadesConfig, setUnidadesConfig] = useState([]);
  const [activeUnidades, setActiveUnidades] = useState([]);

  const [auditorias, setAuditorias] = useState([]);
  const [bloqueios, setBloqueios] = useState([]);

  const auditoriasReminders = useMemo(() => {
    return auditorias.map(a => ({
      ...a,
      start: a.data,
      title: `${a.horario} - ${a.descricao || a.auditoria || 'Auditoria'}`
    }));
  }, [auditorias]);

  const { isTodayOpen, isWeekOpen, todayItems, weekItems, closeTodayPopup, closeWeekPopup, dismissToday, openReminderManually } = useDeadlineReminder(auditoriasReminders, 'agenda_qualidade', 'event');

  // FILTROS AVANÇADOS
  const [filterPendingFuture, setFilterPendingFuture] = useState(true); // A Realizar (Futuro)
  const [filterPendingPast, setFilterPendingPast] = useState(false);     // Não Realizados (Passado)
  const [filterParticipante, setFilterParticipante] = useState('Todos'); // Adicionado Filtro de Participante

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAuditoria, setEditingAuditoria] = useState(null);

  const [isJustificativaModalOpen, setIsJustificativaModalOpen] = useState(false);
  const [auditoriaParaJustificar, setAuditoriaParaJustificar] = useState(null);

  const [isBloqueioModalOpen, setIsBloqueioModalOpen] = useState(false);
  const [isDesbloqueioModalOpen, setIsDesbloqueioModalOpen] = useState(false);

  // PERMISSÕES
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [isLoadingPerms, setIsLoadingPerms] = useState(true);

  // DEBUG MODE
  const [debugMode, setDebugMode] = useState(false);

  const [userName, setUserName] = useState('');
  const currentUser = auth.currentUser;

  // Estados para sistema de notificações
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notificationsCount, setNotificationsCount] = useState(0);

  const monthDays = generateWeekdayGrid(currentDate);

  // ==========================
  // CARREGAR DADOS
  // ==========================
  const [externalEvents, setExternalEvents] = useState([]);

  useEffect(() => {
    // Carregar Auditores
    // USA PRODUÇÃO DIRETAMENTE (Pedido do usuário para ver dados reais)
    const unsubAuditors = onSnapshot(query(collection(db, 'auditors_config'), orderBy('name')), (snap) => {
      const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAuditoresConfig(loaded);

      // Se for a visão pessoal, não define todos como ativos, isso será feito ao carregar o usuário.
      if (!isPersonalView) {
        setActiveAuditores(prev => prev.length === 0 ? loaded.map(a => a.name) : prev);
      }
    }, (error) => console.error("Erro ao carregar auditores:", error));

    // Carregar Unidades
    const unsubUnits = onSnapshot(query(collection(db, 'units_config'), orderBy('name')), (snap) => {
      const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUnidadesConfig(loaded);
      setActiveUnidades(prev => prev.length === 0 ? loaded.map(u => u.name) : prev);
    }, (error) => console.error("Erro ao carregar unidades:", error));

    return () => {
      unsubAuditors();
      unsubUnits();
    };
  }, []);

  // Mapeamento Nome -> UID para filtro de eventos externos
  const [userMap, setUserMap] = useState({});

  useEffect(() => {
    const fetchUsers = async () => {
      const q = query(collection(db, 'users'));
      const snap = await getDocs(q);
      const map = {};
      snap.forEach(doc => {
        const d = doc.data();
        if (d.name) map[d.name] = doc.id;
        // Também mapear displayName se houver
      });
      setUserMap(map);
    };
    fetchUsers();
  }, []);

  // Fetch External Events (Direção)
  useEffect(() => {
    // We only fetch Direção for now as requested. Pessoal might be too noisy, but Conflict usually implies Direção.
    // Fetch ALL Direção Agendado events and filter in-memory by active auditor names.
    // Optimization: filter by date range? For simplicity, fetch all 'Agendado'.
    // USA PRODUÇÃO DIRETAMENTE
    const qDirecao = query(collection(db, 'agendaDirecao')); // Fetch all to filter client side

    const unsubDirecao = onSnapshot(qDirecao, (snap) => {
      const events = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        type: 'external',
        source: 'Direção',
        sourceColl: 'agendaDirecao'
      }));
      setExternalEvents(events);
    }, (error) => {
      console.warn("Sem permissão para ver Agenda Direção (External Events). Ignorando.", error);
      setExternalEvents([]); // Zera para não quebrar
    });

    return () => unsubDirecao();
  }, []);

  // Nome e UID do auditor logado — usados no filtro pessoal
  const [myAuditorName, setMyAuditorName] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoadingPerms(true);
      if (currentUser && currentUser.email) {
        try {
          const usersRef = collection(db, getCollectionName('users'));
          const q = query(usersRef, where('email', '==', currentUser.email));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            setUserName(userData.name);
            setMyAuditorName(userData.agendaName || userData.name || '');

            // Na visão pessoal, o auditor ativo são todos os nomes/aliases atrelados ao usuário logado
            if (isPersonalView) {
              const myAgendaName = userData.agendaName || userData.name;
              const safeMyName = myAgendaName || '';
              const aliases = [safeMyName];
              if (currentUser.email === 'alexandrebareaoliveira@gmail.com' || safeMyName.includes('Oliver') || safeMyName.includes('Alexandre')) {
                aliases.push('Oliver', 'Alexandre', 'Oliver Barea');
              }
              setActiveAuditores([...new Set(aliases)].filter(Boolean));
            }

            // Normalizar: roles (array) ou role (string legada)
            const role = Array.isArray(userData.roles) && userData.roles.length > 0
              ? userData.roles[0] : (userData.role || 'leitor');
            setUserRole(role);
            setPermissions(userData.permissions || {});
          } else {
            setUserRole('leitor');
          }
        } catch (error) {
          console.error("Erro ao buscar permissões:", error);
          setUserRole('leitor');
        }
      } else {
        setUserRole('leitor');
      }
      setIsLoadingPerms(false);
    };

    pedirPermissaoNotificacao();
    fetchUserData();

    const unsubBloqueios = onSnapshot(
      query(collection(db, 'bloqueios')),
      (snapshot) => {
        setBloqueios(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            type: 'bloqueio'
          }))
        );
      }, (error) => console.error("Erro ao buscar bloqueios:", error)
    );

    return () => {
      unsubBloqueios();
    };
  }, [currentUser]);

  // ── useEffect separado: carrega auditorias com filtro inteligente ──────────
  // Roda quando myAuditorName é carregado (assíncrono após login de usuário)
  useEffect(() => {
    if (!currentUser) return;

    const unsubAuditorias = onSnapshot(
      query(collection(db, 'auditorias')),
      (snapshot) => {
        const allDocs = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          type: 'auditoria'
        }));

        if (isPersonalView && myAuditorName) {
          // FILTRO INVISÍVEL — "agenda pessoal" do auditor
          // Mostra apenas eventos onde o auditor logado está designado
          const myName = myAuditorName.trim().toLowerCase();

          setAuditorias(allDocs.filter(ev => {
            // 1. Por UID nos participantesUids
            if ((ev.participantesUids || []).includes(currentUser.uid)) return true;
            // 2. Por UID como criador
            if (ev.criadoPorUid === currentUser.uid) return true;
            // 3. Por nome no campo auditor (legado, string)
            if (typeof ev.auditor === 'string') {
              const evName = ev.auditor.trim().toLowerCase();
              if (evName.includes(myName) || myName.includes(evName)) return true;
            }
            // 4. Por nome no array auditores (padrão atual)
            if (Array.isArray(ev.auditores)) {
              return ev.auditores.some(a => {
                if (typeof a !== 'string') return false;
                const aLow = a.trim().toLowerCase();
                return aLow.includes(myName) || myName.includes(aLow);
              });
            }
            return false;
          }));
        } else if (!isPersonalView) {
          // Visão coletiva: mostra todos
          setAuditorias(allDocs);
        }
        // Se isPersonalView=true mas myAuditorName ainda vazio, aguarda o próximo ciclo
      },
      (error) => console.error("Erro ao buscar auditorias:", error)
    );

    return () => unsubAuditorias();
  }, [currentUser, isPersonalView, myAuditorName]);

  // ==========================
  // NOTIFICAÇÕES
  // ==========================
  useEffect(() => {
    if (userName && (auditorias.length > 0 || bloqueios.length > 0)) {
      const intervalId = setInterval(() => {
        verificarAgendaParaNotificacoes(auditorias, userName);
      }, 60000);
      return () => clearInterval(intervalId);
    }
  }, [auditorias, bloqueios, userName]);

  // ==========================
  // NAVEGAÇÃO
  // ==========================
  const handlePrevMonth = () => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1, 1)));
  const handleNextMonth = () => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1, 1)));

  // ==========================
  // FILTROS
  // ==========================
  const toggleUnidade = (unidadeName) => {
    setActiveUnidades((prev) =>
      prev.includes(unidadeName)
        ? prev.length === 1 ? prev : prev.filter((u) => u !== unidadeName)
        : [...prev, unidadeName]
    );
  };

  const toggleAuditor = (auditorName) => {
    setActiveAuditores((prev) =>
      prev.includes(auditorName)
        ? prev.length === 1 ? prev : prev.filter((a) => a !== auditorName)
        : [...prev, auditorName]
    );
  };

  // ==========================
  // MODAIS
  // ==========================
  const handleOpenModalForNew = () => {
    setEditingAuditoria(null);
    setIsModalOpen(true);
  };
  const handleOpenModalForEdit = (auditoria) => {
    setEditingAuditoria(auditoria);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAuditoria(null);
  };

  // ==========================
  // LOGICA MULTI-AUDITORES
  // ==========================
  // Helper para verificar conflitos considerando array de auditores
  const checkForConflict = async (data, auditoresList, horario, currentEventId = null) => {
    // Verifica bloqueios para QUALQUER um dos auditores
    // Verifica agenda para QUALQUER um dos auditores
    // Firestore 'in' query suporta até 10 itens.
    if (!auditoresList || auditoresList.length === 0) return { conflict: false };

    // Como firestore array-contains-any e in têm limitações, e auditoresList é pequeno, podemos iterar ou fazer query composta.
    // Para simplificar, vou iterar na verificação lógica (menos performático se tiver muitos dados, mas seguro).
    // Mas wait, a função 'checkForConflict' original fazia query no DB.
    // Vamos fazer query para cada auditor selecionado... ou confiar na validação cliente-side se já tivesse os dados carregados? 
    // Não temos todos os dados carregados (bloqueios e auditorias do mês sim, mas e do resto do ano? Recorrencia pode ir longe).

    // Vamos manter simples: Verifica conflito para o PRIMEIRO auditor selecionado por enquanto ou fazer Promise.all

    for (const auditor of auditoresList) {
      const qBloqueio = query(
        collection(db, 'bloqueios'),
        where('auditor', '==', auditor),
        where('data', '==', data)
      );
      const bloqueioSnapshot = await getDocs(qBloqueio);
      if (!bloqueioSnapshot.empty) {
        return { conflict: true, reason: `Dia bloqueado para ${auditor}` };
      }

      // Para auditorias, é mais chato pois 'auditor' no banco pode ser string (legado) ou estar no array 'auditores'.
      // Query ideal: where('auditores', 'array-contains', auditor)
      // Mas legado usa 'auditor' == auditor.
      // Vamos checar ambos.

      const qAuditoriaLegado = query(
        collection(db, 'auditorias'),
        where('auditor', '==', auditor),
        where('data', '==', data),
        where('horario', '==', horario)
      );
      const snapLegado = await getDocs(qAuditoriaLegado);

      const checkDocs = (snap) => {
        let hasConflict = false;
        // Limpa possíveis prefixos para comparar os IDs reais
        const cleanCurrentId = currentEventId ? currentEventId.replace('ext-', '') : null;
        
        snap.forEach(d => {
          const cleanDocId = d.id.replace('ext-', '');
          console.log(`[CONFLICT CHECK] Doc encontrado: ${cleanDocId} | Atual: ${cleanCurrentId} | São iguais? ${cleanDocId === cleanCurrentId}`);
          if (cleanDocId !== cleanCurrentId) {
            hasConflict = true;
          }
        });
        return hasConflict;
      };

      if (checkDocs(snapLegado)) return { conflict: true, reason: `Horário ${horario} já ocupado para ${auditor}` };

      const qAuditoriaArray = query(
        collection(db, 'auditorias'),
        where('auditores', 'array-contains', auditor),
        where('data', '==', data),
        where('horario', '==', horario)
      );
      const snapArray = await getDocs(qAuditoriaArray);
      if (checkDocs(snapArray)) return { conflict: true, reason: `Horário ${horario} já ocupado para ${auditor}` };
    }

    return { conflict: false };
  };

  // ==========================
  // SALVAR / EDITAR
  // ==========================
  const handleAddAuditoria = async (newAuditoria, recorrenciaData) => {
    // newAuditoria.auditores deve ser um array. Se vier só .auditor, converte.
    const auditoresList = newAuditoria.auditores || (newAuditoria.auditor ? [newAuditoria.auditor] : []);

    if (!recorrenciaData.isRecorrente) {
      const conflictResult = await checkForConflict(newAuditoria.data, auditoresList, newAuditoria.horario);
      if (conflictResult.conflict) {
        alert(`Conflito: ${conflictResult.reason}`);
        return;
      }
      const docRef = await addDoc(collection(db, 'auditorias'), {
        ...newAuditoria,
        auditores: auditoresList, // Garante que salva array
        reagendadoVezes: 0,
        status: 'Agendado',
        criadoPorUid: currentUser?.uid || '',
        criadoPorNome: userName || currentUser?.displayName || currentUser?.email || ''
      });

      // CRIAR CONVITES para participantes internos (inviteUtils cuida do roteamento para secretária)
      if (newAuditoria.participantesUids && newAuditoria.participantesUids.length > 0) {
        const { createInvite } = await import('./utils/inviteUtils');
        const creator = {
          uid: currentUser.uid,
          name: userName || currentUser.displayName || currentUser.email,
          email: currentUser.email,
          roles: userRole ? [userRole] : []
        };

        // Buscar dados dos destinatários (precisamos de roles/email para inviteUtils)
        const usersSnap = await getDocs(query(collection(db, 'users')));
        const allUsers = {};
        usersSnap.forEach(d => { allUsers[d.id] = { id: d.id, ...d.data() }; });

        for (const recipientUid of newAuditoria.participantesUids) {
          const recipientData = allUsers[recipientUid];
          if (recipientData) {
            const recipient = {
              uid: recipientUid,
              name: recipientData.name || recipientData.displayName || '',
              email: recipientData.email || '',
              roles: recipientData.roles || []
            };
            try {
              await createInvite({ ...newAuditoria, id: docRef.id }, creator, recipient, 'auditorias');
            } catch (err) {
              console.error('Erro ao criar convite para', recipient.name, err);
            }
          }
        }
      }
      return;
    }

    // Recorrência
    const batch = writeBatch(db);
    let dataAtual = new Date(newAuditoria.data + 'T00:00:00');
    const dataFinal = new Date(recorrenciaData.terminaEm + 'T00:00:00');
    let conflitos = [];

    while (isBefore(dataAtual, dataFinal) || isEqual(dataAtual, dataFinal)) {
      if (dataAtual.getDay() !== 6 && dataAtual.getDay() !== 0) {
        const dataStr = dataAtual.toISOString().split('T')[0];

        // Aqui não vou checar conflito batch a batch para não demorar séculos no UI.
        // Vou confiar que o usuário resolverá conflitos depois se houver, ou que o backend rules barraria.
        // Mas o usuário pediu qualidade. Vamos checar.

        const conflict = await checkForConflict(dataStr, auditoresList, newAuditoria.horario);
        if (conflict.conflict) {
          conflitos.push(`${dataStr}: ${conflict.reason}`);
        } else {
          const docRef = doc(collection(db, 'auditorias'));
          batch.set(docRef, {
            ...newAuditoria,
            data: dataStr,
            auditores: auditoresList,
            reagendadoVezes: 0,
            status: 'Agendado',
            criadoPorUid: currentUser?.uid || '',
            criadoPorNome: userName || currentUser?.displayName || currentUser?.email || ''
          });
        }
      }

      // Incremento
      switch (recorrenciaData.frequencia) {
        case 'diaria': dataAtual = addDays(dataAtual, 1); break;
        case 'semanal': dataAtual = addWeeks(dataAtual, 1); break;
        case 'quinzenal': dataAtual = addWeeks(dataAtual, 2); break;
        case 'mensal': dataAtual = addMonths(dataAtual, 1); break;
        default: dataAtual = addDays(dataFinal, 1);
      }
    }

    if (conflitos.length > 0) {
      alert(`Alguns agendamentos recorrentes não foram criados por conflito:\n${conflitos.join('\n')}`);
    }
    await batch.commit();
  };

  const handleUpdateAuditoria = async (auditoriaOriginal, dadosAtualizados) => {
    const collectionName = auditoriaOriginal.sourceColl || 'auditorias';
    const realId = (auditoriaOriginal.id || '').replace('ext-', '');

    // Checar conflito na data DESTINO (novo dia/horário)
    if (collectionName === 'auditorias') {
      const auditoresListUpdate = dadosAtualizados.auditores || (dadosAtualizados.auditor ? [dadosAtualizados.auditor] : []);
      const auditoresListOriginal = auditoriaOriginal.auditores || (auditoriaOriginal.auditor ? [auditoriaOriginal.auditor] : []);
      
      const hasTimeChanged = dadosAtualizados.data !== auditoriaOriginal.data || 
                             dadosAtualizados.horario !== auditoriaOriginal.horario ||
                             JSON.stringify([...auditoresListUpdate].sort()) !== JSON.stringify([...auditoresListOriginal].sort());

      if (hasTimeChanged) {
        // Passamos realId para EXCLUIR o próprio evento da verificação
        const conflictResult = await checkForConflict(dadosAtualizados.data, auditoresListUpdate, dadosAtualizados.horario, realId);
        if (conflictResult.conflict) {
          alert(`Não foi possível reagendar: ${conflictResult.reason}`);
          throw new Error(conflictResult.reason);
        }
      }
    }

    const auditoriaRef = doc(db, collectionName, realId);
    let reagendadoVezes = auditoriaOriginal.reagendadoVezes || 0;
    if (auditoriaOriginal.data !== dadosAtualizados.data) reagendadoVezes++;

    const dataParaSalvar = { ...dadosAtualizados, reagendadoVezes };

    try {
      await updateDoc(auditoriaRef, dataParaSalvar);
    } catch (updateError) {
      if (updateError.code === 'not-found') {
        // Documento foi deletado mas ainda aparece na tela: recria com setDoc
        console.warn('[UPDATE] Documento não encontrado, recriando com setDoc:', realId);
        await setDoc(auditoriaRef, {
          ...dataParaSalvar,
          status: auditoriaOriginal.status || 'Agendado',
          criadoPorUid: auditoriaOriginal.criadoPorUid || '',
          criadoPorNome: auditoriaOriginal.criadoPorNome || ''
        });
      } else {
        throw updateError;
      }
    }

    // Notificar participantes de forma NÃO-bloqueante
    notifyParticipantsOfChange(
      auditoriaOriginal,
      dadosAtualizados,
      userName || currentUser?.displayName || 'Usuário',
      'auditorias'
    ).catch(e => console.warn('[NOTIFY] Erro ao notificar, mas evento foi salvo:', e));

    // Sincronizar mudança de data/hora/descrição nas cópias da agenda_pessoal
    // Busca por AMBOS os campos: 'sourceRef' (cópias de aceite) e 'originalEventId' (legado)
    const dateChanged = dadosAtualizados.data && dadosAtualizados.data !== auditoriaOriginal.data;
    const timeChanged = dadosAtualizados.horario && dadosAtualizados.horario !== auditoriaOriginal.horario;
    const descChanged = dadosAtualizados.descricao && dadosAtualizados.descricao !== auditoriaOriginal.descricao;

    if (dateChanged || timeChanged || descChanged) {
      try {
        const updates = {};
        if (dateChanged) updates.data = dadosAtualizados.data;
        if (timeChanged) updates.horario = dadosAtualizados.horario;
        if (descChanged) updates.descricao = dadosAtualizados.descricao;

        // Buscar por 'sourceRef' (cópias criadas por aceite de convite - novo padrão)
        const qBySourceRef = query(collection(db, 'agenda_pessoal'), where('sourceRef', '==', realId));
        const snapBySourceRef = await getDocs(qBySourceRef);

        // Buscar por 'originalEventId' (cópias legadas)
        const qByOriginalId = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', realId));
        const snapByOriginalId = await getDocs(qByOriginalId);

        // Unir sem duplicar por ID
        const copiasPorId = new Map();
        snapBySourceRef.forEach(d => copiasPorId.set(d.id, d));
        snapByOriginalId.forEach(d => copiasPorId.set(d.id, d));

        const updatePromises = [];
        copiasPorId.forEach((d) => {
          updatePromises.push(updateDoc(doc(db, 'agenda_pessoal', d.id), updates));
        });

        if (updatePromises.length > 0) {
          await Promise.all(updatePromises);
          console.log(`[SYNC-QUALIDADE] ${updatePromises.length} cópia(s) na agenda_pessoal atualizadas.`);
        }
      } catch (pesErr) {
        console.error('[SYNC-QUALIDADE] Erro ao propagar mudança para agenda_pessoal:', pesErr);
      }
    }
  };

  const handleDeleteAuditoria = async (id) => {
    try {
      const collectionName = editingAuditoria?.sourceColl || 'auditorias';
      const realId = id.startsWith('ext-') ? id.replace('ext-', '') : id;

      console.log(`[Apagar] Excluindo evento ${realId} da coleção ${collectionName} (SuperAdmin/Admin)`);

      await deleteDoc(doc(db, collectionName, realId));

      // Limpeza profunda de clones
      try {
        await deleteDoc(doc(db, 'agenda_pessoal', 'ext-' + realId));
      } catch (e) { }

      try {
        const qDir = query(collection(db, 'agendaDirecao'), where('sourceRef', '==', realId));
        const snapDir = await getDocs(qDir);
        snapDir.forEach(async (d) => await deleteDoc(doc(db, 'agendaDirecao', d.id)));

        const qPes = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', realId));
        const snapPes = await getDocs(qPes);
        snapPes.forEach(async (d) => await deleteDoc(doc(db, 'agenda_pessoal', d.id)));
      } catch (e) { }

      alert('Evento removido com sucesso!');
      setIsModalOpen(false);
    } catch (error) {
      console.error("Erro ao excluir auditoria/evento:", error);
      alert("Erro ao excluir: " + error.message + "\n\nSe você não for o criador deste evento, pode não ter permissão para excluí-lo.");
    }
  };

  const handleSaveBloqueio = async (bloqueioData) => {
    // Verificar se já existe
    const q = query(
      collection(db, 'bloqueios'),
      where('auditor', '==', bloqueioData.auditor),
      where('data', '==', bloqueioData.data)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      alert('Dia já bloqueado para este auditor.');
      return;
    }
    await addDoc(collection(db, 'bloqueios'), bloqueioData);
  };

  const handleStatusChange = async (newStatus) => {
    if (!editingAuditoria) {
      console.error('[STATUS] editingAuditoria é null/undefined!');
      return;
    }
    console.log('[STATUS] Mudando status para:', newStatus, 'ID:', editingAuditoria.id, 'sourceColl:', editingAuditoria.sourceColl);

    if (newStatus === 'Não Realizado') {
      setAuditoriaParaJustificar(editingAuditoria);
      setIsJustificativaModalOpen(true);
      setIsModalOpen(false);
    } else {
      try {
        // Limpar prefixo 'ext-' do ID se presente
        const realId = editingAuditoria.id.startsWith('ext-')
          ? editingAuditoria.id.replace('ext-', '')
          : editingAuditoria.id;

        const targetColl = editingAuditoria.sourceColl || 'auditorias';
        const docRef = doc(db, targetColl, realId);

        try {
          await updateDoc(docRef, {
            status: newStatus,
            dataConclusao: new Date().toISOString().split('T')[0],
            justificativaNaoRealizado: null
          });
          console.log('[STATUS] Status atualizado em:', targetColl);
        } catch (e) {
          if (e.code === 'not-found') {
            console.warn('[STATUS] Documento não encontrado, recriando com setDoc:', realId);
            const dataToSave = { ...editingAuditoria };
            delete dataToSave.id;
            delete dataToSave.type;
            delete dataToSave.isExternal;
            delete dataToSave.readOnly;
            delete dataToSave.source;
            delete dataToSave.sourceColl;
            
            await setDoc(docRef, {
              ...dataToSave,
              status: newStatus,
              dataConclusao: new Date().toISOString().split('T')[0],
              justificativaNaoRealizado: null
            });
            console.log('[STATUS] Documento recriado e status atualizado em:', targetColl);
          } else {
            console.error('[STATUS] Erro:', e);
            alert('Não foi possível atualizar o status: ' + e.message);
            return;
          }
        }

        // --- NOVO: Propagar o status para a agenda_pessoal ---
        try {
          const qPes = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', realId));
          const snapPes = await getDocs(qPes);
          snapPes.forEach(async (d) => {
            await updateDoc(doc(db, 'agenda_pessoal', d.id), {
              status: newStatus,
              dataConclusao: new Date().toISOString().split('T')[0],
              justificativaNaoRealizado: null
            });
          });
        } catch (pesErr) {
          console.error('[STATUS] Erro ao propagar status para agenda_pessoal:', pesErr);
        }
        // --------------------------------------------------------

        handleCloseModal();
      } catch (err) {
        console.error('[STATUS] Erro ao atualizar status:', err);
        alert('Erro ao atualizar status: ' + err.message);
      }
    }
  };

  const handleSaveJustificativa = async (justificativa) => {
    if (!auditoriaParaJustificar) return;

    const realId = auditoriaParaJustificar.id.startsWith('ext-')
      ? auditoriaParaJustificar.id.replace('ext-', '')
      : auditoriaParaJustificar.id;

    const targetColl = auditoriaParaJustificar.sourceColl || 'auditorias';
    const docRef = doc(db, targetColl, realId);

    try {
      await updateDoc(docRef, {
        justificativaNaoRealizado: justificativa,
        status: 'Não Realizado',
        dataConclusao: null
      });
      console.log('[JUSTIF] Status atualizado em:', targetColl);
    } catch (e) {
      if (e.code === 'not-found') {
        console.warn('[JUSTIF] Documento não encontrado, recriando com setDoc:', realId);
        const dataToSave = { ...auditoriaParaJustificar };
        delete dataToSave.id;
        delete dataToSave.type;
        delete dataToSave.isExternal;
        delete dataToSave.readOnly;
        delete dataToSave.source;
        delete dataToSave.sourceColl;
        
        await setDoc(docRef, {
          ...dataToSave,
          justificativaNaoRealizado: justificativa,
          status: 'Não Realizado',
          dataConclusao: null
        });
        console.log('[JUSTIF] Documento recriado e status atualizado em:', targetColl);
      } else {
        console.error('[JUSTIF] Erro:', e);
        alert('Não foi possível salvar a justificativa: ' + e.message);
        return;
      }
    }

    // --- NOVO: Propagar justificativa para a agenda_pessoal ---
    try {
      const qPes = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', realId));
      const snapPes = await getDocs(qPes);
      snapPes.forEach(async (d) => {
        await updateDoc(doc(db, 'agenda_pessoal', d.id), {
          status: 'Não Realizado',
          justificativaNaoRealizado: justificativa,
          dataConclusao: null
        });
      });
    } catch (pesErr) {
      console.error('[STATUS] Erro ao propagar justificativa para agenda_pessoal:', pesErr);
    }
    // --------------------------------------------------------

    setIsJustificativaModalOpen(false);
    setAuditoriaParaJustificar(null);
    handleCloseModal();
  };

  const handleDesbloquearDia = async (data, auditor) => {
    if (!window.confirm(`Desbloquear o dia ${data} para ${auditor}?`)) return;

    const q = query(collection(db, 'bloqueios'), where('data', '==', data), where('auditor', '==', auditor));
    const snap = await getDocs(q);
    snap.forEach(async (d) => {
      await deleteDoc(doc(db, 'bloqueios', d.id));
    });
  };

  // ==========================
  // RENDER HELPERS
  // ==========================
  const getCorUnidade = (nomeUnidade) => {
    let unidadeNormalizada = nomeUnidade || '';
    if (['Home/Senior', 'Home / Senior', 'Início/Idosos', 'Inicio/Idosos'].includes(nomeUnidade)) {
      unidadeNormalizada = 'Homecare';
    } else if (nomeUnidade && !['Prontobaby', 'Homecare', 'CPL', 'CPB'].includes(nomeUnidade)) {
      unidadeNormalizada = 'Prontobaby';
    }
    const u = unidadesConfig.find(uc => uc.name === unidadeNormalizada);
    return u ? u.color : '#888';
  };

  const getCorAuditor = (nomeAuditor) => {
    const a = auditoresConfig.find(ac => ac.name === nomeAuditor);
    return a ? a.color : '#ccc';
  };

  const isSuper = currentUser && currentUser.email === 'alexandrebareaoliveira@gmail.com';
  const isAdmin = userRole === 'admin';
  const canEdit = isSuper || isAdmin || (permissions['agenda_qualidade']?.edit === true);

  if (isLoadingPerms) return <div className="agenda-container"><p>Carregando permissões...</p></div>;

  const renderMobileLayout = () => {
    return (
      <div className="agenda-mobile-list">
        {isPersonalView && (
          <div className="page-header mb-4 text-center">
            <h2 style={{ fontWeight: '700', color: '#2d3436', fontSize: '2.2rem' }}>Minha Agenda</h2>
            <p className="text-muted">Acompanhe seus agendamentos, reuniões e auditorias.</p>
          </div>
        )}
        <div className="agenda-controls mb-3" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
          <button onClick={handlePrevMonth} className="btn btn-sm btn-outline-secondary">&lt;</button>
          <h4 className="mb-0 text-capitalize">{format(currentDate, 'MMMM yyyy', { locale: ptBR })}</h4>
          <button onClick={handleNextMonth} className="btn btn-sm btn-outline-secondary">&gt;</button>
        </div>

        {monthDays.filter(day => day.getMonth() === currentDate.getMonth()).map(day => {
          const dayString = day.toISOString().split('T')[0];

          const audsDia = auditorias.filter(a => a.data === dayString);
          const extDia = externalEvents.filter(e => {
            if (e.data !== dayString) return false;
            const responsaveis = e.responsaveis || [];
            const participantes = e.participantes || ''; // String manual
            const participantesnomes = e.participantesNomes || []; // Array de nomes (novo)
            const participantesUids = e.participantesUids || []; // Array de UIDs

            // O filtro deve passar se ALGUM dos activeAuditores estiver no evento
            return activeAuditores.some(auditorName => {
              // 1. Checa por nome (Exato ou Parcial)
              // Ex: auditorName="Mirelli", no evento="Mirelli Terra" -> Match!
              const nameMatch = responsaveis.some(r => r.includes(auditorName) || auditorName.includes(r)) ||
                participantes.includes(auditorName) ||
                participantesnomes.some(pN => pN.includes(auditorName) || auditorName.includes(pN));

              // 2. Checa por UID (Tentativa Inteligente)
              // Se userMap tem "Mirelli Terra" -> UID, e auditorName é "Mirelli", tentamos achar a key que contém "Mirelli"
              let auditorUid = userMap[auditorName];

              if (!auditorUid) {
                // Busca reversa no mapa: procurar chave que contenha o nome do filtro
                const realNameKey = Object.keys(userMap).find(k => k.includes(auditorName) || auditorName.includes(k));
                if (realNameKey) {
                  auditorUid = userMap[realNameKey];
                }
              }

              const uidMatch = auditorUid && participantesUids.includes(auditorUid);

              return nameMatch || uidMatch;
            });
          }).map(e => ({ ...e, type: 'external', isExternal: true }));

          // ====== DEDUPLICAÇÃO FORTE ======
          // Filtra itens externos que já possuem uma cópia física na coleção auditorias neste dia
          const extDiaDedup = extDia.filter(extEv => {
            const cleanExtText = (extEv.descricao || extEv.auditoria || '').toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 25);
            const cleanExtTime = (extEv.horario || 'notime').trim();

            // Verifica se já existe um evento em audsDia com o mesmo sourceRef ou originalEventId ou texto equivalente
            const hasPhysicalCopy = audsDia.some(audEv => {
              if (audEv.originalEventId === extEv.id || audEv.sourceRef === extEv.id) return true;

              const cleanAudText = (audEv.descricao || audEv.auditoria || '').toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 25);
              const cleanAudTime = (audEv.horario || 'notime').trim();

              return cleanExtText.length > 5 && cleanExtText === cleanAudText && cleanExtTime === cleanAudTime;
            });
            return !hasPhysicalCopy;
          });

          const eventosDia = [...audsDia, ...extDiaDedup].sort((a, b) => (a.horario || '').localeCompare(b.horario || ''));
          const bloqueioDia = bloqueios.find(b => b.data === dayString);

          if (eventosDia.length === 0 && !bloqueioDia) return null;

          return (
            <div key={dayString} className="mobile-day-group" style={{ marginBottom: '15px', backgroundColor: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '8px', marginBottom: '10px' }}>
                <span style={{ fontWeight: 'bold', color: '#00897b', textTransform: 'capitalize' }}>{format(day, 'EEEE, dd/MM', { locale: ptBR })}</span>
                {bloqueioDia && <span className="badge bg-danger">Bloqueado: {bloqueioDia.auditor}</span>}
              </div>

              {eventosDia.length > 0 ? (
                <div className="mobile-events-list">
                  {eventosDia.map(auditoria => {
                    if (auditoria.type === 'external') {
                      return (
                        <div
                          key={auditoria.id}
                          className="mobile-event-card"
                          style={{ padding: '10px', backgroundColor: '#f0f0f0', marginBottom: '8px', borderRadius: '6px', borderLeft: `4px solid #7f8c8d`, color: '#555' }}
                          onClick={() => {
                            const mappedEvent = {
                              ...auditoria,
                              readOnly: true,
                              auditores: auditoria.responsaveis || [],
                              auditor: (auditoria.responsaveis && auditoria.responsaveis.length > 0) ? auditoria.responsaveis[0] : '',
                              auditoria: auditoria.descricao,
                              categoria: 'Agendamento Externo'
                            };
                            handleOpenModalForEdit(mappedEvent);
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong>{auditoria.horario}</strong>
                            <span className="badge bg-secondary">Externo</span>
                          </div>
                          <div style={{ marginTop: '5px', fontWeight: 'bold' }}>{auditoria.descricao}</div>
                          <div style={{ fontSize: '0.9em' }}>{auditoria.source}</div>
                        </div>
                      );
                    }
                    const statusClass = getAuditoriaStatusClass(auditoria);
                    return (
                      <div
                        key={auditoria.id}
                        className="mobile-event-card"
                        style={{ padding: '10px', backgroundColor: '#f9f9f9', marginBottom: '8px', borderRadius: '6px', borderLeft: `4px solid ${getCorAuditor(auditoria.auditor)}`, color: '#333' }}
                        onClick={() => handleOpenModalForEdit(auditoria)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <strong>{auditoria.horario}</strong>
                          <span className="badge bg-light text-dark">{auditoria.auditor}</span>
                        </div>
                        <div style={{ marginTop: '5px' }}>{auditoria.unidade} - {auditoria.setor}</div>
                        <div className="small text-muted">{auditoria.descricao || auditoria.auditoria}</div>
                        <div className="small mt-1">Status: {auditoria.status}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-muted small text-center">Apenas Bloqueio</div>
              )}
            </div>
          );
        })}

        {auditorias.filter(a => {
          const d = new Date(a.data + 'T00:00:00');
          return d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
        }).length === 0 && (
            <div className="text-center p-4 text-muted">Nenhum agendamento neste mês.</div>
          )}
      </div>
    );
  };

  const btnBaseStyle = {
    padding: '8px 14px', borderRadius: '8px', border: 'none', 
    fontWeight: '600', cursor: 'pointer', display: 'inline-flex', 
    alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: '#fff',
    transition: 'all 0.2s'
  };

  return (
    <div className="agenda-container" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      
      {/* === HEADER E FILTROS CONGELADOS === */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100, 
        backgroundColor: '#f8fafc', padding: '20px 20px 10px 20px', 
        marginLeft: '-20px', marginRight: '-20px', marginTop: '-20px',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ color: '#1e293b', margin: 0, fontSize: '1.4rem', fontWeight: '800' }}>
            {isPersonalView ? 'Minha Agenda' : 'Agenda da Qualidade'}
          </h2>
          <div className="header-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button style={{...btnBaseStyle, backgroundColor: '#f59e0b'}} onClick={openReminderManually}>
              🔔 Próximos Vencimentos
            </button>
            {canEdit && <button style={{...btnBaseStyle, backgroundColor: '#eab308'}} onClick={() => setIsBloqueioModalOpen(true)}>Bloquear Dia</button>}
            {canEdit && <button style={{...btnBaseStyle, backgroundColor: '#64748b'}} onClick={() => setIsDesbloqueioModalOpen(true)}>Desbloquear Dia</button>}
            {canEdit && <button style={{...btnBaseStyle, backgroundColor: '#10b981'}} onClick={handleOpenModalForNew}>+ Novo Agendamento</button>}
          </div>
        </div>

        {!isMobile && (
          <div className="agenda-controls" style={{
            display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
            alignItems: 'center', gap: '15px', backgroundColor: '#fff',
            padding: '10px 20px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            border: '1px solid #e2e8f0', marginBottom: '10px'
          }}>
            {/* Navegação de Mês */}
            <div className="week-navigation" style={{ display: 'flex', alignItems: 'center', gap: '5px', margin: 0 }}>
              <button onClick={handlePrevMonth} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', color: '#475569', borderRadius: '6px', padding: '4px 10px' }}>&lt;</button>
              <span style={{ fontSize: '15px', fontWeight: '700', minWidth: '140px', textAlign: 'center', textTransform: 'capitalize', color: '#334155' }}>
                {currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
              </span>
              <button onClick={handleNextMonth} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', color: '#475569', borderRadius: '6px', padding: '4px 10px' }}>&gt;</button>
            </div>

            {/* Filtros de Pendência (Checkboxes Compactos) e Participantes */}
            <div className="filter-area" style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 600, color: '#444' }}>
                <input type="checkbox" checked={filterPendingFuture} onChange={e => setFilterPendingFuture(e.target.checked)} style={{accentColor: '#10b981'}} />
                A Realizar
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 600, color: '#444' }}>
                <input type="checkbox" checked={filterPendingPast} onChange={e => setFilterPendingPast(e.target.checked)} style={{accentColor: '#ef4444'}} />
                Não Realizados
              </label>
              
              <div style={{ marginLeft: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#444' }}>Participante:</span>
                <select 
                  value={filterParticipante} 
                  onChange={e => setFilterParticipante(e.target.value)}
                  style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#334155', backgroundColor: '#f8fafc', outline: 'none', cursor: 'pointer' }}
                >
                  <option value="Todos">Todos</option>
                  {Object.keys(userMap).sort().map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Filtros de Unidade e Auditor (Compacto, alinhado à direita) */}
            <div className="filter-tabs-container" style={{ display: 'flex', gap: '15px', margin: 0, padding: 0, width: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <div className="unidade-tabs" style={{ gap: '6px' }}>
                {unidadesConfig.map((u) => {
                  const isActive = activeUnidades.includes(u.name);
                  return (
                    <button
                      key={u.id}
                      className={`auditor-checkbox-item ${isActive ? 'active' : ''}`}
                      style={{
                        backgroundColor: isActive ? u.color : '#fff',
                        color: isActive ? '#fff' : '#64748b',
                        borderColor: isActive ? u.color : '#e2e8f0',
                        padding: '4px 12px', borderRadius: '20px',
                        fontSize: '0.8rem', fontWeight: isActive ? '700' : '600',
                        boxShadow: isActive ? `0 2px 6px ${u.color}40` : 'none',
                      }}
                      onClick={() => toggleUnidade(u.name)}
                    >
                      {!isActive && <span className="auditor-color-dot" style={{ backgroundColor: u.color }}></span>}
                      {u.name}
                    </button>
                  );
                })}
              </div>
              {!isPersonalView && (
                <>
                  <div style={{ width: '1.5px', background: '#e2e8f0', height: '20px' }}></div>
                  <div className="auditor-tabs" style={{ gap: '6px' }}>
                    {auditoresConfig.map((a) => {
                      const isActive = activeAuditores.includes(a.name);
                      return (
                        <button
                          key={a.id}
                          className={`auditor-checkbox-item ${isActive ? 'active' : ''}`}
                          style={{
                            backgroundColor: isActive ? a.color : '#f8fafc',
                            color: isActive ? '#fff' : '#64748b',
                            borderColor: isActive ? a.color : '#e2e8f0',
                            padding: '4px 12px', borderRadius: '20px',
                            fontSize: '0.8rem', fontWeight: isActive ? '700' : '600',
                            boxShadow: isActive ? `0 2px 6px ${a.color}40` : 'none',
                          }}
                          onClick={() => toggleAuditor(a.name)}
                        >
                          {!isActive && <span className="auditor-color-dot" style={{ backgroundColor: a.color }}></span>}
                          {a.name}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <AddAuditoriaModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddAuditoria={handleAddAuditoria}
        onUpdateAuditoria={handleUpdateAuditoria}
        onDeleteAuditoria={handleDeleteAuditoria}
        onStatusChange={handleStatusChange}
        editingAuditoria={editingAuditoria}
        unidadesConfig={unidadesConfig} // Passando configs inteiras
        auditoresConfig={auditoresConfig}
      />

      <JustificativaModal isOpen={isJustificativaModalOpen} onClose={() => setIsJustificativaModalOpen(false)} onSave={handleSaveJustificativa} />
      <BloquearDiaModal isOpen={isBloqueioModalOpen} onClose={() => setIsBloqueioModalOpen(false)} onSave={handleSaveBloqueio} auditoresList={auditoresConfig.map(a => a.name)} />
      <DesbloquearDiaModal isOpen={isDesbloqueioModalOpen} onClose={() => setIsDesbloqueioModalOpen(false)} bloqueios={bloqueios} onDesbloquear={handleDesbloquearDia} />

      {/* Scrollable Main Area */}
      <main className="agenda-main" style={{ flex: 1, overflowY: 'auto' }}>
        {isMobile ? renderMobileLayout() : (
          <>

            <div className="agenda-week-header">
              <div>Segunda</div><div>Terça</div><div>Quarta</div><div>Quinta</div><div>Sexta</div>
            </div>

            <div className="agenda-grid-monthly">
              {monthDays.map((day) => {
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                const dayString = day.toISOString().split('T')[0];
                const isPast = day < new Date().setHours(0, 0, 0, 0);

                // FILTRO DE ITENS - ESTRATEGIA DEBUG
                // Em vez de filtrar e remover, vamos classificar para o render
                const todosItensDoDia = auditorias.filter(a => a.data === dayString);

                // External Events
                const externalDoDia = externalEvents.filter(e => {
                  if (e.data !== dayString) return false;

                  const responsaveis = Array.isArray(e.responsaveis) ? e.responsaveis : (e.responsaveis ? [e.responsaveis] : []);
                  const participantes = e.participantes || ''; // String manual
                  const participantesnomes = Array.isArray(e.participantesNomes) ? e.participantesNomes : (e.participantesNomes ? [e.participantesNomes] : []);
                  const participantesUids = Array.isArray(e.participantesUids) ? e.participantesUids : (e.participantesUids ? [e.participantesUids] : []);

                  // O filtro deve passar se ALGUM dos activeAuditores estiver no evento E tiver aceito
                  return activeAuditores.some(auditorName => {
                    let auditorUid = userMap[auditorName];

                    if (!auditorUid) {
                      const realNameKey = Object.keys(userMap).find(k => k.includes(auditorName) || auditorName.includes(k));
                      if (realNameKey) {
                        auditorUid = userMap[realNameKey];
                      }
                    }

                    const isResponsavel = responsaveis.some(r => typeof r === 'string' && (r.includes(auditorName) || auditorName.includes(r)));

                    const isParticipantName = typeof participantes === 'string' && participantes.includes(auditorName) || participantesnomes.some(pN => typeof pN === 'string' && (pN.includes(auditorName) || auditorName.includes(pN)));
                    const isParticipantUid = auditorUid && participantesUids.includes(auditorUid);
                    const isParticipant = isParticipantName || isParticipantUid;

                    const aceites = Array.isArray(e.aceitePor) ? e.aceitePor : (e.aceitePor ? [e.aceitePor] : []);
                    const aceitesUids = Array.isArray(e.aceitePorUids) ? e.aceitePorUids : (e.aceitePorUids ? [e.aceitePorUids] : []);
                    const isAcceptedName = aceites.some(a => typeof a === 'string' && (a.includes(auditorName) || auditorName.includes(a)));
                    const isAcceptedUid = auditorUid && aceitesUids.includes(auditorUid);
                    const isAccepted = isAcceptedName || isAcceptedUid;

                    // Mostra na Qualidade se: for o responsável direto, ou se for participante E já aceitou o convite
                    return isResponsavel || (isParticipant && isAccepted);
                  });
                }).map(e => ({ ...e, type: 'external', isExternal: true }));

                const itemsProcessados = todosItensDoDia.map(a => {
                  let rejectionReason = null;

                  // 1. Filtro Unidade
                  if (a.unidade) {
                    let unidadeNormalizada = a.unidade;
                    if (['Home/Senior', 'Home / Senior', 'Início/Idosos', 'Inicio/Idosos'].includes(a.unidade)) {
                      unidadeNormalizada = 'Homecare';
                    } else if (!['Prontobaby', 'Homecare', 'CPL', 'CPB'].includes(a.unidade)) {
                      // Se for uma unidade desconhecida (ex: "TI", "Manutenção"), considera como Prontobaby
                      unidadeNormalizada = 'Prontobaby';
                    }

                    if (!activeUnidades.includes(unidadeNormalizada)) {
                      rejectionReason = `Unidade: ${a.unidade} (Norm: ${unidadeNormalizada})`;
                    }
                  }

                  // 2. Filtro Auditor
                  let audsRaw = a.auditores || a.auditor || [];
                  const auds = Array.isArray(audsRaw) ? audsRaw : [audsRaw];
                  if (!rejectionReason) {
                    // CORREÇÃO: Match Parcial para aceitar "Mirelli Terra" se "Mirelli" estiver ativo
                    const temAuditorAtivo = auds.some(audit =>
                      activeAuditores.some(ativo =>
                        audit.includes(ativo) || ativo.includes(audit)
                      )
                    );

                    if (!temAuditorAtivo) {
                      rejectionReason = `Auditor: ${auds.join(', ')}`;
                    }
                  }

                  // 3. Filtros de Pendência (A Realizar / Não Realizados)
                  const isFilterActive = filterPendingFuture || filterPendingPast;

                  if (!rejectionReason && isFilterActive) {
                    const isPendingFuture = !isPast && a.status !== 'Feito' && a.status !== 'Reagendado';
                    const isPendingPast = isPast && (a.status === 'Não Realizado' || !a.status);

                    let matchesFuture = filterPendingFuture && isPendingFuture;
                    let matchesPast = filterPendingPast && isPendingPast;

                    // Se apenas um estiver ativo, mostramos apenas o que bate com aquele.
                    // Se ambos estiverem ativos, é união.
                    if (!matchesFuture && !matchesPast) {
                      // Mas espera, se eu selecionei "Apenas Futuro", não quero ver "Passado Pendente" a menos que "Apenas Passado" esteja selecionado.
                      // A lógica acima ja cobre isso.
                      // matchesFuture só é true se o filtro ta on E o item bate.
                      // Se nada bateu, rejeita.

                      // Detalhe do rejection reason para debug
                      if (!filterPendingFuture && isPendingFuture) rejectionReason = 'Filtro: Futuro OFF';
                      else if (!filterPendingPast && isPendingPast) rejectionReason = 'Filtro: Passado OFF';
                      else rejectionReason = 'Filtro: Status/Data';
                    }
                  }

                  // 4. Filtros de Participante Analisados
                  if (!rejectionReason && filterParticipante !== 'Todos') {
                    const pNomes = Array.isArray(a.participantesNomes) ? a.participantesNomes : [];
                    const pNomesStr = pNomes.join(' ').toLowerCase();
                    const pDescStr = (typeof a.participantes === 'string' ? a.participantes : '').toLowerCase();
                    const searchPart = filterParticipante.toLowerCase();
                    const audsStr = (Array.isArray(a.auditores) ? a.auditores.join(' ') : (a.auditor || '')).toLowerCase();
                    
                    if (!pNomesStr.includes(searchPart) && !pDescStr.includes(searchPart) && !audsStr.includes(searchPart)) {
                       rejectionReason = `Participante: ${filterParticipante} não incluso`;
                    }
                  }

                  // Sanitizar participantes para render na frente do card
                  const allParticipantsStr = [
                      ...(Array.isArray(a.participantesNomes) ? a.participantesNomes : []),
                      ...(typeof a.participantes === 'string' && a.participantes ? [a.participantes] : [])
                  ].filter(Boolean).join(', ');

                  return { ...a, type: 'auditoria', rejectionReason, _displayParticipants: allParticipantsStr };
                });

                // Filtra os que devem aparecer (sem reject ou debugMode on)
                const itemsVisible = itemsProcessados.filter(item => !item.rejectionReason || debugMode);
                const itemsExternal = externalDoDia.filter(ext => {
                  if (filterParticipante === 'Todos') return true;
                  const pNomes = Array.isArray(ext.participantesNomes) ? ext.participantesNomes : [];
                  const pNomesStr = pNomes.join(' ').toLowerCase();
                  const pDescStr = (typeof ext.participantes === 'string' ? ext.participantes : '').toLowerCase();
                  const audsStr = (Array.isArray(ext.responsaveis) ? ext.responsaveis.join(' ') : (ext.responsaveis || '')).toLowerCase();
                  const searchPart = filterParticipante.toLowerCase();
                  return pNomesStr.includes(searchPart) || pDescStr.includes(searchPart) || audsStr.includes(searchPart);
                });

                const bloqueiosDoDia = bloqueios.filter((b) =>
                  b.data === dayString && activeAuditores.includes(b.auditor)
                ).map(b => ({ ...b, type: 'bloqueio' }));

                // Deduplicar: remover external events cujo ID já está em auditorias ou que já têm cópia física
                const itemsExternalDedup = itemsExternal
                  .filter(extEv => {
                    // Verifica se já existe um evento em itemsVisible com o mesmo id, sourceRef original,
                    // ou texto equivalente.
                    const cleanExtText = (extEv.descricao || extEv.auditoria || '').toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 25);
                    const cleanExtTime = (extEv.horario || 'notime').trim();

                    const hasPhysicalCopy = itemsVisible.some(audEv => {
                      if (audEv.id === extEv.id || audEv.originalEventId === extEv.id || audEv.sourceRef === extEv.id) return true;

                      const cleanAudText = (audEv.descricao || audEv.auditoria || '').toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 25);
                      const cleanAudTime = (audEv.horario || 'notime').trim();

                      return cleanExtText.length > 5 && cleanExtText === cleanAudText && cleanExtTime === cleanAudTime;
                    });

                    return !hasPhysicalCopy;
                  })
                  .map(e => ({ ...e, id: 'ext-' + e.id })); // Prefixo para evitar key duplicada no React

                const itemsDoDia = [...itemsVisible, ...itemsExternalDedup, ...bloqueiosDoDia].sort((a, b) =>
                  (a.horario || '00:00').localeCompare(b.horario || '23:59')
                );

                return (
                  <div key={day.toISOString()} className={`day-cell ${isCurrentMonth ? '' : 'not-current-month'}`}>
                    <div className="day-cell-header">
                      <span className="day-number">{day.getDate()}</span>
                      {debugMode && <small style={{ fontSize: '9px', color: 'red' }}> ({itemsVisible.length})</small>}
                    </div>
                    <div className="day-cell-body">
                      {itemsDoDia.length === 0 && <div className="empty-day"></div>}
                      {itemsDoDia.map((item, index) => {
                        if (!item || item.isManualPlaceholder) return null;

                        if (item.rejectionReason) {
                          return (
                            <div key={item.id} style={{
                              background: '#ffe6e6',
                              border: '1px solid red',
                              fontSize: '9px',
                              padding: '2px',
                              marginBottom: '2px',
                              color: 'red'
                            }}>
                              ❌ {item.rejectionReason} <br />
                              <strong>{item.auditor}</strong> - {item.unidade} - {item.data}
                            </div>
                          );
                        }

                        if (item.type === 'bloqueio') {
                          return (
                            <div key={item.id} className="bloqueio-card" onClick={() => isCurrentMonth && handleDesbloquearDia(item.data, item.auditor)} title="Clique para desbloquear">
                              <strong>{item.auditor}</strong>: {item.motivo} 🗑️
                            </div>
                          );
                        }

                        if (item.type === 'external') {
                          return (
                            <div
                              key={`${item.id}-${index}-ext`}
                              className="auditoria-card"
                              style={{
                                backgroundColor: '#f0f0f0',
                                borderLeft: '4px solid #7f8c8d',
                                cursor: 'pointer', // Habilita cursor de clique
                                position: 'relative'
                              }}
                              onClick={() => {
                                // Mapeia os campos da Direção para o formato que o Modal de Auditoria entende
                                const mappedEvent = {
                                  ...item,
                                  // Permite alterar status (Realizado/Não Realizado) mesmo sendo evento externo
                                  auditores: item.responsaveis || [], // Mapeia responsáveis para auditores
                                  auditor: (item.responsaveis && item.responsaveis.length > 0) ? item.responsaveis[0] : '',
                                  auditoria: item.descricao, // Mapeia descrição
                                  categoria: 'Agendamento Externo'
                                };
                                handleOpenModalForEdit(mappedEvent);
                              }}
                              title={`Externo: ${item.descricao} (${item.source})`}
                            >
                              <div className="auditoria-time" style={{ color: '#555' }}>{item.horario}</div>
                              <div className="auditoria-info">
                                {/* Mostra a descrição real e a origem */}
                                <strong style={{ color: '#333' }}>{item.descricao}</strong>
                                <div style={{ fontSize: '0.85em', color: '#666' }}>({item.source})</div>
                                <div style={{ fontSize: '0.8em', color: '#888' }}>
                                  {item.participantesNomes ? item.participantesNomes.join(', ') : (item.participantes || '')}
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Renderizar Auditoria
                        let itemAudsRaw = item.auditores || item.auditor || [];
                        const auds = Array.isArray(itemAudsRaw) ? itemAudsRaw : [itemAudsRaw];
                        const corBorda = getCorUnidade(item.unidade);

                        return (
                          <div
                            key={`${item.id}-${index}-int`}
                            className={`auditoria-card ${getAuditoriaStatusClass(item)}`}
                            style={{ borderLeftColor: isCurrentMonth ? corBorda : '#aaa' }}
                            onClick={() => isCurrentMonth && handleOpenModalForEdit(item)}
                          >
                            <div className="auditoria-card-header">
                              <strong>{item.horario}</strong> - {item.descricao || item.auditoria}
                            </div>
                            <div className="auditoria-card-body">
                              <p><strong>Auditor(es):</strong> {auds.join(', ')}</p>
                              {item.setor && <p><strong>Setor:</strong> {item.setor}</p>}
                              {item._displayParticipants && (
                                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                  <strong>Participantes:</strong> {item._displayParticipants}
                                </p>
                              )}
                            </div>
                            {item.reagendadoVezes > 0 && <div className="reagendado-aviso">Reagendado {item.reagendadoVezes}x</div>}
                            {item.justificativaNaoRealizado && (
                              <div className="justificativa-balao"><strong>Motivo:</strong> {item.justificativaNaoRealizado}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
      <DeadlineReminder
        isTodayOpen={isTodayOpen}
        isWeekOpen={isWeekOpen}
        todayItems={todayItems}
        weekItems={weekItems}
        onCloseTodayPopup={closeTodayPopup}
        onCloseWeekPopup={closeWeekPopup}
        onDismissToday={dismissToday}
        type="event"
      />
    </div>
  );
}

export default AgendaQualidade;
